import { useState, useMemo } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Eyebrow, Rule } from "@/components/editorial";
import { cn } from "@/lib/utils";
import type { NutriScore, PremiumFilters } from "@/types/menu";

const NUTRI_OPTIONS: NutriScore[] = ["A", "B", "C", "D", "E"];

const KCAL_MIN = 300;
const KCAL_MAX = 1200;
const KCAL_STEP = 50;
const KCAL_DEFAULT = 700;

/**
 * Premium filter sheet — the actual "Le Cahier" feature surface.
 *
 * Free users never see this directly; they hit the PaywallSheet first
 * (the parent button delegates). Premium users can dial Nutri-Score
 * minimum, Éco-Score minimum, seasonal-only, and a max-kcal-per-meal cap.
 * Submitting passes the filters back to the parent, which re-runs the
 * brigade with them.
 */
export function PremiumFiltersSheet({
  open,
  onOpenChange,
  initial,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: PremiumFilters | null;
  onApply: (filters: PremiumFilters) => void;
}) {
  const [nutri, setNutri] = useState<NutriScore | null>(
    initial?.nutri_min ?? null,
  );
  const [eco, setEco] = useState<NutriScore | null>(initial?.eco_min ?? null);
  const [seasonal, setSeasonal] = useState<boolean>(
    initial?.seasonal_only ?? false,
  );
  const [maxKcal, setMaxKcal] = useState<number>(
    initial?.max_kcal_per_meal ?? KCAL_DEFAULT,
  );
  const [maxKcalOn, setMaxKcalOn] = useState<boolean>(
    initial?.max_kcal_per_meal != null,
  );

  function apply() {
    onApply({
      nutri_min: nutri,
      eco_min: eco,
      seasonal_only: seasonal,
      max_kcal_per_meal: maxKcalOn ? maxKcal : null,
    });
    onOpenChange(false);
  }

  function reset() {
    setNutri(null);
    setEco(null);
    setSeasonal(false);
    setMaxKcal(KCAL_DEFAULT);
    setMaxKcalOn(false);
  }

  const activeCount =
    Number(Boolean(nutri)) +
    Number(Boolean(eco)) +
    Number(seasonal) +
    Number(maxKcalOn);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none">
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <Eyebrow accent>
              <Sparkles className="size-3" /> Filtres Cahier
            </Eyebrow>
            <Dialog.Close
              aria-label="Fermer"
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <div className="px-6 py-6">
            <Dialog.Title className="font-serif text-[28px] font-normal leading-[1.05] tracking-[-0.015em]">
              Affinez votre <span className="italic">semaine</span>.
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-[13px] text-ink-50">
              Ces contraintes seront transmises à la brigade au prochain run.
            </Dialog.Description>

            <Rule className="my-5" />

            <FilterGroup
              title="Nutri-Score minimum"
              hint={nutri ? `≥ ${nutri}` : "Sans contrainte"}
            >
              <ScoreRow
                options={NUTRI_OPTIONS}
                value={nutri}
                onChange={setNutri}
              />
            </FilterGroup>

            <FilterGroup
              title="Éco-Score minimum"
              hint={eco ? `≥ ${eco}` : "Sans contrainte"}
            >
              <ScoreRow options={NUTRI_OPTIONS} value={eco} onChange={setEco} />
            </FilterGroup>

            <FilterGroup
              title="Saisonnalité"
              hint={
                seasonal ? "Produits de saison uniquement" : "Tout autorisé"
              }
            >
              <Toggle
                label="Produits de saison uniquement"
                active={seasonal}
                onClick={() => setSeasonal((s) => !s)}
              />
            </FilterGroup>

            <FilterGroup
              title="Plafond calorique"
              hint={maxKcalOn ? `${maxKcal} kcal max / repas` : "Sans plafond"}
            >
              <div className="flex items-center gap-3">
                <Toggle
                  label="Plafonner"
                  active={maxKcalOn}
                  onClick={() => setMaxKcalOn((s) => !s)}
                  compact
                />
                <div className="flex-1 opacity-100" data-disabled={!maxKcalOn}>
                  <Slider
                    value={useMemo(() => [maxKcal], [maxKcal])}
                    min={KCAL_MIN}
                    max={KCAL_MAX}
                    step={KCAL_STEP}
                    onValueChange={(v) => setMaxKcal(v[0] ?? KCAL_DEFAULT)}
                    aria-label="Calories maximum par repas"
                    disabled={!maxKcalOn}
                    className={cn(!maxKcalOn && "opacity-40")}
                  />
                </div>
              </div>
            </FilterGroup>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-hair px-6 py-4">
            <Button variant="ghost" size="sm" onClick={reset}>
              Réinitialiser
            </Button>
            <Button onClick={apply}>
              Appliquer{activeCount > 0 ? ` (${activeCount})` : ""}
              <Check />
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Building blocks ──────────────────────────────────────────────────

function FilterGroup({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <div className="mb-2.5 flex items-baseline justify-between">
        <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-ink-50">
          {title}
        </h3>
        <span className="font-serif text-[13px] italic text-ink-50">
          {hint}
        </span>
      </div>
      {children}
    </div>
  );
}

function ScoreRow({
  options,
  value,
  onChange,
}: {
  options: NutriScore[];
  value: NutriScore | null;
  onChange: (next: NutriScore | null) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {options.map((s) => {
        const selected = value === s;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(selected ? null : s)}
            aria-pressed={selected}
            className={cn(
              "h-10 flex-1 font-serif text-[15px] transition-colors",
              selected
                ? "bg-foreground text-background"
                : "border border-hair bg-background text-foreground hover:border-foreground",
            )}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({
  label,
  active,
  onClick,
  compact,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-2 rounded-pill border px-3.5 py-2 text-[12.5px] font-medium transition-colors",
        compact ? "shrink-0" : "w-full justify-center",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-hair bg-background text-ink-70 hover:border-ink-30",
      )}
    >
      {active && <Check className="size-3" strokeWidth={2.5} />}
      {label}
    </button>
  );
}
